import { endOfDay, getTime, startOfDay } from 'date-fns';
import { formatNumberEnsuringTwoDigits } from 'helpers/text';

interface responseProps {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
  second: string;
  toInputDate: string;
  formattedDate: string;
  formattedHour: string;
}

const SHAPE_RESPONSE_PROPS = {
  day: '--',
  month: '--',
  year: '----',
  hour: '--',
  minute: '--',
  second: '--',
  toInputDate: '----/--/--',
  formattedDate: '--/--/----',
  formattedHour: '--:--:--',
};

interface TimeProps {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

const SHAPE_TIME_PROPS: TimeProps = {
  days: '--',
  hours: '--',
  minutes: '--',
  seconds: '--',
};

const TIMESTAMP_INITIAL_YEAR = 1970;

export function formatTimestamp(
  timestampDate: number | Date | string | undefined
): responseProps {
  if (!timestampDate) return SHAPE_RESPONSE_PROPS;

  const date = new Date(timestampDate);
  const fullYear = date.getFullYear();

  if (fullYear < TIMESTAMP_INITIAL_YEAR) return SHAPE_RESPONSE_PROPS;

  const day = ('00' + date.getDate()).slice(-2);
  const month = ('00' + (date.getMonth() + 1)).slice(-2);
  const year = ('0000' + fullYear).slice(-4);
  const hour = ('00' + date.getHours()).slice(-2);
  const minute = ('00' + date.getMinutes()).slice(-2);
  const second = ('00' + date.getSeconds()).slice(-2);

  return {
    day,
    month,
    year,
    hour,
    minute,
    second,
    toInputDate: `${year}-${month}-${day}`,
    formattedDate: `${day}/${month}/${year}`,
    formattedHour: `${hour}:${minute}:${second}`,
  };
}

export function formatMillisecondsToHours(value: number) {
  const seconds = value / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;

  const secondsText = formatNumberEnsuringTwoDigits(Math.floor(seconds % 60));
  const minutesText = formatNumberEnsuringTwoDigits(Math.floor(minutes % 60));
  const hoursText = formatNumberEnsuringTwoDigits(Math.floor(hours));

  return {
    hours: hoursText,
    minutes: minutesText,
    seconds: secondsText,
  };
}

export function formatMillisecondsToDays(value: number): TimeProps {
  if (!value || value < 0) return SHAPE_TIME_PROPS;

  const { seconds, minutes, hours } = formatMillisecondsToHours(value);

  const hoursNumber = Number(hours);

  const days = hoursNumber / 24;

  const hoursText = formatNumberEnsuringTwoDigits(Math.floor(hoursNumber % 24));
  const daysText = formatNumberEnsuringTwoDigits(Math.floor(days));

  return {
    days: daysText,
    hours: hoursText,
    minutes,
    seconds,
  };
}

export function newDateByString(date: string): Date {
  const [year, month, day] = date.split('-');

  return new Date(Number(year), Number(month) - 1, Number(day));
}

interface GetInitialDateParams {
  date?: string;
  timeDifference?: number;
}

export function getInitialDate({
  date,
  timeDifference,
}: GetInitialDateParams = {}): number {
  const defaultTimeDifference = timeDifference ? timeDifference : 15;
  let newDate = new Date();

  if (date) {
    newDate = newDateByString(date);
  } else {
    newDate.setDate(newDate.getDate() - defaultTimeDifference);
  }

  newDate.setHours(0);
  newDate.setMinutes(0);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  return newDate.getTime();
}

export function getFinalDate(date: string = ''): number {
  let newDate = new Date();

  if (date) {
    newDate = newDateByString(date);
  }

  newDate.setHours(23);
  newDate.setMinutes(59);
  newDate.setSeconds(59);
  newDate.setMilliseconds(999);

  return newDate.getTime();
}

export function clearDate(date: Date = new Date()): number {
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date.getTime();
}

/**
 *
 * @param {Date} date - JS Date object. It's priority over the other params.
 * @param {number} hours - Decimal two case hour value. Default = 0.
 * @param {number} minutes - Decimal two case minutes value. Default = 0.
 * @param {number} seconds - Decimal two case seconds value. Default = 0.
 * @returns {number} The sum of hours, minutes and seconds in milliseconds
 */
export function convertToMilliseconds({
  date,
  hours = 0,
  minutes = 0,
  seconds = 0,
}: {
  date?: Date;
  hours?: number;
  minutes?: number;
  seconds?: number;
}): number {
  const ONE_HOUR_MILISECONDS = 3600000;

  const hour = date ? date.getHours() : hours;
  const minute = date ? date.getMinutes() : minutes;
  const second = date ? date.getSeconds() : seconds;

  const hourToMilliseconds = hour * ONE_HOUR_MILISECONDS;
  const minuteToMilliseconds = (minute / 60) * ONE_HOUR_MILISECONDS;
  const secondToMilliseconds = (second / 60 ** 2) * ONE_HOUR_MILISECONDS;

  return hourToMilliseconds + minuteToMilliseconds + secondToMilliseconds;
}

/**
 *
 * @param {number} time - time in timestamp format
 * @returns {Date} the formatted time in Date format
 */
export function convertTimestampToDate(time: number): Date {
  return new Date(time);
}

/**
 *
 * @param {Date | string} date - date can be in string or Date format
 * @param {'start' | 'end'} period - can be 'start' or 'end'
 * @returns {number} the formatted timestamp
 */
export function convertDateToTimestamp(
  date: Date | string,
  period?: 'start' | 'end'
): number {
  const dateToConvert = new Date(date);

  switch (period) {
    case 'start':
      const startDate = startOfDay(dateToConvert);
      return getTime(startDate);
    case 'end':
      const endDate = endOfDay(dateToConvert);
      return getTime(endDate);
    default:
      return getTime(dateToConvert);
  }
}

/**
 * Formats a Date object to Brazilian locale string format (dd/mm/yyyy)
 *
 * @param {Date | null | undefined} date - The date to format. Can be null or undefined.
 * @returns {string} The formatted date string in Brazilian format or empty string if date is null/undefined
 *
 * @example
 * formatDateToBR(new Date('2023-12-25')) // returns '25/12/2023'
 * formatDateToBR(null) // returns ''
 * formatDateToBR(undefined) // returns ''
 */
export function formatDateToBR(date?: Date | null): string {
  return date?.toLocaleDateString('pt-BR') || '';
}
